import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import { log } from '../utils/logger';
import './AdminSettings.css';

const AdminSettings = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/site-settings/admin/all');
      setSettings(response.data.settings || []);
      
      if (!response.data.settings || response.data.settings.length === 0) {
        setError('Nenhuma configuração encontrada. Execute o script SQL para criar as configurações padrão.');
      }
    } catch (error) {
      if (error.response?.data?.code === 'TABLE_NOT_FOUND') {
        setError('Tabela de configurações não existe. Execute o script SQL no servidor.');
      } else if (error.response?.status === 404) {
        setError(`Endpoint não encontrado (404). URL: ${error.config?.baseURL}${error.config?.url}. Verifique se a rota está configurada no servidor.`);
      } else if (error.response?.status === 401) {
        setError('Não autenticado. Faça login novamente.');
      } else if (error.response?.status === 403) {
        setError('Acesso negado. Você precisa ser super administrador (IsAdmin = 1).');
      } else if (!error.response) {
        setError('Erro de rede. Verifique se o servidor está online.');
      } else {
        setError(error.response?.data?.error || `Erro ao carregar configurações (${error.response?.status}). Verifique os logs do servidor.`);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/admin');
      return;
    }
    if (!user.isAdmin) {
      navigate('/');
      return;
    }
    fetchSettings();
  }, [user, navigate, authLoading, fetchSettings]);

  const handleToggle = async (key, currentValue) => {
    try {
      setSaving(true);
      setMessage('');
      setError('');
      
      const newValue = !currentValue;
      await api.put(`/site-settings/admin/${key}`, { value: newValue });
      
      // Atualizar estado local
      setSettings(prev => prev.map(s => 
        s.key === key ? { ...s, value: newValue } : s
      ));
      
      setMessage(`Configuração "${key}" atualizada com sucesso!`);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      log.error('Erro ao atualizar configuração:', error);
      setError(error.response?.data?.error || 'Erro ao atualizar configuração');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-settings-page">
        <div className="admin-container">
          <div className="loading">Carregando configurações...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-settings-page">
      <div className="admin-container">
        <div className="admin-header">
          <button className="btn-back" onClick={() => navigate('/admin/painel')}>
            ← Voltar ao Painel Admin
          </button>
          <h1>Configurações do Site</h1>
          <p className="admin-subtitle">Ative ou desative funcionalidades do site</p>
        </div>

        {message && (
          <div className="message success">{message}</div>
        )}
        {error && (
          <div className="message error">{error}</div>
        )}

        <div className="settings-grid">
          {settings.map((setting) => (
            <div key={setting.key} className="setting-card">
              <div className="setting-header">
                <h3>{getSettingTitle(setting.key)}</h3>
                <div className={`toggle-switch ${setting.value ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={setting.value}
                    onChange={() => handleToggle(setting.key, setting.value)}
                    disabled={saving}
                    id={`toggle-${setting.key}`}
                  />
                  <label htmlFor={`toggle-${setting.key}`}>
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
              <p className="setting-description">{setting.description}</p>
              <div className="setting-status">
                <span className={`status-badge ${setting.value ? 'enabled' : 'disabled'}`}>
                  {setting.value ? '✓ Ativado' : '✗ Desativado'}
                </span>
                {setting.updated_at && (
                  <span className="setting-updated">
                    Atualizado: {new Date(setting.updated_at).toLocaleString('pt-BR')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {settings.length === 0 && (
          <div className="empty-state">
            <p>Nenhuma configuração encontrada. Execute o script SQL para criar as configurações padrão.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const getSettingTitle = (key) => {
  const titles = {
    'mixmall_enabled': 'Módulo legado (desativado)',
    'online_points_shop_enabled': 'Módulo legado (desativado)',
    'online_points_cron_enabled': 'Módulo legado (desativado)',
    'recharge_enabled': 'Módulo legado (desativado)',
    'recharge_pix_enabled': 'Módulo legado (desativado)',
    'recharge_card_enabled': 'Módulo legado (desativado)',
    'recharge_stripe_enabled': 'Módulo legado (desativado)',
    'registration_enabled': 'Criação de contas'
  };
  return titles[key] || key;
};

export default AdminSettings;
